import Dropkick from 'dropkickjs';
import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['select', 'time', 'ticket', 'tooltip', 'timezone'];

  connect() {
    this.removePastEvents();
    this.overlay = document.querySelector('.js-embed-overlay');
    this.closeBtn = document.querySelector('.js-close-embed-overlay');
    this.iframe = document.querySelector('.js-embed-iframe');
    this.closeBtn.addEventListener('click', this.close.bind(this));
    if (this.hasSelectTarget) {
      // Show the first ticket for the selected date on page load
      this.showFirstTicketForDate(this.selectTarget.value);
    }
    this.selectFirstShowtime()
  }

  open(e) {
    e.preventDefault();
    let url = e.currentTarget.dataset.embedUrl;
    if (url == '') return;
    this.overlay.classList.add('is-open');
    this.iframe.setAttribute('src', url);
  }

  close(e) {
    e.preventDefault();
    this.overlay.classList.remove('is-open');
    this.iframe.setAttribute('src', '');
  }

  // Since the page is cached for up to a month we need to remove past events
  // from the dropdown client-side. This function will remove past events from
  // the dropdown and the past showtimes.
  removePastEvents() {
    let targets = '.js-event-timestamp';
    let now = Date.now();
    targets = document.querySelectorAll(targets);
    if (targets.length == 0) return;

    targets.forEach((el) => {
      let date = this.getDate(el);
      if (date < now) el.remove();
    });
    let dateOptions = this.selectTarget.querySelectorAll('option');
    dateOptions.forEach((option) => {
      let optionValue = option.value;
      let times = document.querySelectorAll(`.js-event-timestamp[data-date="${optionValue}"]`);
      // Remove the date if there are no showtimes
      if (times.length == 0) option.remove();
    });
    this.initDropkick();
  }

  getDate(el) {
    let elDate = el.getAttribute('data-date').trim();
    let time = el.getAttribute('data-time').trim();
    time = this.convertTime12to24(time)
    let timezone = this.timezoneTarget.value;
    let dateString = `${elDate}T${time}:00${timezone}`;
    let date = Date.parse(dateString);
    return date
  }

  convertTime12to24(time12h) {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours) < 10 ? '0' + hours : hours;
    if (hours === '12') hours = '00';
    if (modifier === 'PM') hours = parseInt(hours, 10) + 12;
    return `${hours}:${minutes}`;
  }

  initDropkick() {
    this.initializeDropdownElements(this.selectTarget.value);
    if (this.selectTarget.querySelectorAll('option').length == 0) {
      this.selectTarget.remove();
    } else {
      new Dropkick(this.selectTarget, {
        mobile: true,
        initialize: this.initializeDropdownElements.bind(this),
        change: () => {
          this.initializeDropdownElements(this.selectTarget.value);
        },
      });
    }
  }

  /**
   * Show the selected date
   * - Hide all times, except the selected date
   * @param value
   */
  initializeDropdownElements(value) {
    if (!value) return;

    // Hide all times, except the selected date
    this.timeTargets.forEach((el) => {
      // Get the date for each time
      let elDate = el.getAttribute('data-date')?.trim();
      if (!elDate) return;
      // Show/hide the time, depending on the selected date
      el.style.display = elDate.startsWith(value) ? 'inline' : 'none';
    });

    // Show the first ticket for the selected date
    this.showFirstTicketForDate(value);

    // Select the first showtime, if available
    this.selectFirstShowtime();
  }

  /**
   * Show the selected time
   * - Hide all tickets, except the selected time
   * - Disable all tickets, until a time is selected
   * @param event
   */
  time(event) {
    // Get the selected date and time
    const selectedDate = event.target.getAttribute('data-date')?.trim();
    const selectedTime = event.target.getAttribute('data-time')?.trim();

    if (!selectedDate || !selectedTime) return;

    // Remove the active class from all times, and add it to the selected time
    this.timeTargets.forEach((el) => el.classList.toggle('is-selected', el === event.target));

    this.ticketTargets.forEach((el) => {
      // Get the date and time for each ticket
      const elDate = el.getAttribute('data-date')?.trim();
      const elTime = el.getAttribute('data-time')?.trim();

      if (!elDate || !elTime) return;

      // Determine if the ticket link should be active
      const isActive = elDate === selectedDate && elTime === selectedTime;

      // Show/hide the ticket link and update the disabled state
      if (el.classList.contains('event-info__cta')) {
        el.style.display = isActive ? 'inline-block' : 'none';
      } else {
        el.style.display = isActive ? 'flex' : 'none';
      }

      el.disabled = !isActive ? 'disabled' : null;
      el.classList.toggle('is-disabled', !isActive);

      // Hide the tooltip if a valid time is selected
      if (isActive) {
        this.tooltipTarget?.classList.add('is-hidden');
      }
    });
  }

  /**
   * Show the first ticket for the selected date
   * - Disable all tickets, until a time is selected
   * @param date
   */
  showFirstTicketForDate(date) {
    let firstTicketShown = false;
    this.ticketTargets.forEach((el) => {
      const elDate = el.getAttribute('data-date')?.trim();
      if (!elDate) return;

      // Determine if this is the first ticket for the selected date
      const shouldShow = elDate.startsWith(date) && !firstTicketShown;

      // Show + enable the first ticket for the selected date, and hide + disable all others
      el.style.display = shouldShow ? 'flex' : 'none';
      el.disabled = 'disabled';
      el.classList.add('is-disabled');

      // Show/hide the tooltip, depending on whether a valid time is selected or not
      if (shouldShow) {
        this.tooltipTarget?.classList.add('is-hidden');
        firstTicketShown = true;
      } else {
        this.tooltipTarget?.classList.remove('is-hidden');
      }
    });
  }

  /**
   * Select the first showtime, if available
   * - This is used to automatically select the first showtime, when the page loads
   */
  selectFirstShowtime() {
    const firstShowtime = this.timeTargets.find(el => el.style.display !== 'none');

    if (firstShowtime) {
      firstShowtime.classList.add('is-selected');
      this.time({ target: firstShowtime });
    }
  }

}
